<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import { analyzePassword, type ZxcvbnResult } from '../../services/crypto/passwordStrength'
import { formatGuessCount } from '../../utils/formatGuessCount'

const props = defineProps<{
  password: string
  username: string
  email: string
}>()

const emit = defineEmits<{
  (e: 'update:score', score: number | null): void
}>()

const result = ref<ZxcvbnResult | null>(null)
const isAnalyzing = ref(false)
let debounceTimer: ReturnType<typeof setTimeout> | null = null
let requestGeneration = 0

const userInputs = computed(() => [
  props.username,
  props.email,
  'Superelectric Ink',
  'SuperelectricInk',
  'superelectric-ink',
].filter(Boolean))

const scoreLabels = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'] as const
const scoreLabel = computed(() => result.value ? scoreLabels[result.value.score] : '')

async function runAnalysis() {
  const password = props.password
  const generation = ++requestGeneration
  if (!password) {
    result.value = null
    isAnalyzing.value = false
    return
  }

  isAnalyzing.value = true
  try {
    const next = await analyzePassword(password, userInputs.value)
    if (generation === requestGeneration && password === props.password) result.value = next
  } catch {
    if (generation === requestGeneration) result.value = null
  } finally {
    if (generation === requestGeneration) isAnalyzing.value = false
  }
}

watch(
  () => [props.password, props.username, props.email],
  () => {
    if (debounceTimer) clearTimeout(debounceTimer)
    if (!props.password) {
      void runAnalysis()
      return
    }
    debounceTimer = setTimeout(() => void runAnalysis(), 250)
  },
  { immediate: true },
)

watch(result, (next) => {
  emit('update:score', next ? next.score : null)
})

onUnmounted(() => {
  if (debounceTimer) clearTimeout(debounceTimer)
  requestGeneration++
})
</script>

<template>
  <section v-if="password" class="password-strength-report" aria-live="polite">
    <div class="password-strength-heading">
      <span>Password strength</span>
      <span v-if="isAnalyzing" class="password-strength-status">Checking…</span>
      <span v-else-if="result" class="password-strength-status">{{ scoreLabel }} · {{ result.score }}/4</span>
    </div>

    <div v-if="result" class="password-score-track" :aria-label="`Password score ${result.score} out of 4`">
      <span
        v-for="index in 4"
        :key="index"
        class="password-score-segment"
        :class="{ active: index <= result.score }"
      ></span>
    </div>

    <div v-if="result" class="password-result-row">
      <div class="password-feedback">
        <p v-if="result.feedback.warning">{{ result.feedback.warning }}</p>
        <ul v-if="result.feedback.suggestions.length">
          <li v-for="suggestion in result.feedback.suggestions" :key="suggestion">{{ suggestion }}</li>
        </ul>
      </div>
      <p class="password-guesses">Guesses to crack: <strong>{{ formatGuessCount(result.guesses) }}</strong></p>
    </div>
  </section>
</template>

<style scoped>
.password-strength-report {
  padding-top: 3px;
  font-size: 11.5px;
  color: var(--text-normal);
}

.password-strength-heading {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-weight: 600;
}

.password-strength-status {
  color: var(--text-muted);
}

.password-score-track {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
  margin: 8px 0 10px;
}

.password-score-segment {
  height: 4px;
  border-radius: 2px;
  background: rgba(var(--white-rgb), 0.1);
}

.password-score-segment.active {
  background: var(--accent);
}

.password-result-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

.password-feedback p,
.password-feedback ul,
.password-guesses {
  margin: 0;
}

.password-feedback ul {
  padding-left: 17px;
}

.password-feedback p + ul {
  margin-top: 4px;
}

.password-guesses {
  color: var(--text-muted);
  text-align: right;
}

.password-guesses strong {
  color: var(--text-normal);
  font-weight: 600;
}

@media (max-width: 620px) {
  .password-result-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .password-guesses {
    text-align: left;
  }
}
</style>
